import { useRecordContext, useTranslate } from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { Badge } from "@/components/ui/badge";

import { TopToolbar } from "../layout/TopToolbar";
import type { ChampClass } from "../types";
import { ActiveClassesToggle } from "./ActiveClassesToggle";
import { formatHeadcount, formatTimeRange } from "./classUtils";

const filters = [
  <SearchInput key="q" source="q" alwaysOn />,
  <ReferenceInput
    key="program"
    source="program_id"
    reference="programs"
    alwaysOn
  >
    <SelectInput optionText="name" helperText={false} />
  </ReferenceInput>,
];

const ClassListActions = () => (
  <TopToolbar>
    <ActiveClassesToggle />
    <ExportButton />
    <CreateButton label="resources.classes.action.new" />
  </TopToolbar>
);

const ScheduleField = (_props: { label?: string }) => {
  const record = useRecordContext<ChampClass>();
  const translate = useTranslate();
  if (!record) return null;
  const day = record.day_of_week
    ? translate(`resources.classes.days.${record.day_of_week}`)
    : "";
  return (
    <span>
      {[day, formatTimeRange(record.start_time, record.end_time)]
        .filter(Boolean)
        .join(" · ")}
    </span>
  );
};

const HeadcountField = (_props: { label?: string }) => {
  const record = useRecordContext<ChampClass>();
  const translate = useTranslate();
  if (!record) return null;
  const isFull = record.spots_left === 0;
  return (
    <div className="flex items-center gap-2">
      <span>{formatHeadcount(record)}</span>
      {isFull && (
        <Badge variant="destructive">
          {translate("resources.classes.roster.full")}
        </Badge>
      )}
    </div>
  );
};

export const ClassList = () => (
  <List
    title={false}
    filters={filters}
    filterDefaultValues={{ active: true }}
    actions={<ClassListActions />}
    sort={{ field: "program_name", order: "ASC" }}
    perPage={50}
  >
    <DataTable rowClick="show" bulkActionButtons={false}>
      <DataTable.Col source="name" />
      <DataTable.Col source="program_name" />
      <DataTable.Col label="resources.classes.fields.schedule" disableSort>
        <ScheduleField />
      </DataTable.Col>
      <DataTable.Col source="location" />
      <DataTable.Col source="coach_name" />
      <DataTable.Col source="nb_enrolled">
        <HeadcountField />
      </DataTable.Col>
      <DataTable.NumberCol source="spots_left" />
      <DataTable.NumberCol source="nb_waitlist" />
      <DataTable.NumberCol source="nb_unpaid" />
    </DataTable>
  </List>
);
